/**
 * SectionAccordion
 *
 * Collapsible section showing header and question list when expanded.
 * Displays questions in navigation flow order with tree indentation.
 */

import React, { useMemo } from 'react';
import QuestionRow from './QuestionRow';
import PlannerConfigService from '../../../services/plannerConfigService';
import { ChevronRight, ChevronDown, Repeat, AlertCircle } from 'lucide-react';

const SECTION_ICONS = {
  ponds: '💧',
  grading: '📐',
  curb: '🛣️',
  walls: '🧱',
  ditches: '〰️',
  sidewalks: '🚶',
  'building-pads': '🏗️',
  'grate-elevations': '⬇️',
  'ada-ramps': '♿'
};

const SectionAccordion = ({
  sectionKey,
  section,
  expanded,
  onToggle,
  onUpdateQuestion,
  onUpdateSection,
  allQuestionIds,
  validationErrors = []
}) => {
  const questionEntries = Object.entries(section.questions || {});
  const hasErrors = validationErrors.length > 0;
  const icon = SECTION_ICONS[sectionKey] || '📋';

  // Build navigation flow order with depth info
  const flowOrder = useMemo(() => {
    return PlannerConfigService.getQuestionFlowOrder(section);
  }, [section]);

  return (
    <div style={{
      background: 'var(--dark-surface)',
      border: `1px solid ${hasErrors ? 'var(--red-500)' : 'var(--gray-700)'}`,
      borderRadius: 'var(--radius-lg)',
      overflow: 'hidden'
    }}>
      {/* Section Header */}
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${section.label || sectionKey} section`}
        style={{
          width: '100%',
          padding: 'var(--space-3) var(--space-4)',
          background: expanded ? 'var(--gray-800)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          textAlign: 'left',
          transition: 'background 0.15s'
        }}
      >
        {/* Expand/Collapse Icon */}
        {expanded ? (
          <ChevronDown size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
        ) : (
          <ChevronRight size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
        )}

        {/* Section Icon */}
        <span style={{ fontSize: '1.2em' }}>{icon}</span>

        {/* Section Name */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--text-base)',
            fontWeight: 'var(--font-medium)',
            color: 'var(--gray-100)'
          }}>
            {section.label || sectionKey}
          </div>
          <div style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--gray-500)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 2
          }}>
            <span>{questionEntries.length} questions</span>
            {section.type === 'looped' && (
              <>
                <span style={{ color: 'var(--gray-600)' }}>•</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Repeat size={10} />
                  looped
                </span>
              </>
            )}
            <span style={{ color: 'var(--gray-600)' }}>•</span>
            <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>
              start: {section.initialQuestion}
            </span>
          </div>
        </div>

        {/* Error Indicator */}
        {hasErrors && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--red-400)',
            fontSize: 'var(--text-xs)'
          }}>
            <AlertCircle size={14} />
            {validationErrors.length}
          </div>
        )}
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div style={{
          borderTop: '1px solid var(--gray-700)',
          padding: 'var(--space-3)'
        }}>
          {/* Section Settings */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-3)',
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--gray-800)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ color: 'var(--gray-400)' }}>Label:</span>
              <input
                type="text"
                value={section.label || ''}
                onChange={(e) => onUpdateSection(sectionKey, { label: e.target.value })}
                style={{
                  background: 'var(--gray-900)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '4px 8px',
                  color: 'var(--gray-100)',
                  fontSize: 'var(--text-sm)',
                  width: '150px'
                }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ color: 'var(--gray-400)' }}>Initial:</span>
              <select
                value={section.initialQuestion || ''}
                onChange={(e) => onUpdateSection(sectionKey, { initialQuestion: e.target.value })}
                style={{
                  background: 'var(--gray-900)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '4px 8px',
                  color: 'var(--gray-100)',
                  fontSize: 'var(--text-sm)'
                }}
              >
                {questionEntries.map(([qId]) => (
                  <option key={qId} value={qId}>{qId}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                type="checkbox"
                checked={section.type === 'looped'}
                onChange={(e) => onUpdateSection(sectionKey, { type: e.target.checked ? 'looped' : 'single' })}
                style={{ accentColor: 'var(--cta-blue)' }}
              />
              <span style={{ color: 'var(--gray-400)' }}>Looped</span>
            </label>
          </div>

          {/* Questions List - Tree View */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
            {flowOrder.map((node) => {
              const question = section.questions[node.id];
              if (!question) return null;
              const questionErrors = validationErrors.filter(e => e.question === node.id);
              return (
                <QuestionRow
                  key={node.id}
                  questionId={node.id}
                  question={question}
                  sectionKey={sectionKey}
                  onUpdate={(updates) => onUpdateQuestion(sectionKey, node.id, updates)}
                  allQuestionIds={allQuestionIds}
                  errors={questionErrors}
                  depth={node.depth}
                  branch={node.branch}
                />
              );
            })}
            {/* Show orphan questions not in flow */}
            {questionEntries
              .filter(([qId]) => !flowOrder.some(n => n.id === qId))
              .map(([questionId, question]) => {
                const questionErrors = validationErrors.filter(e => e.question === questionId);
                return (
                  <QuestionRow
                    key={questionId}
                    questionId={questionId}
                    question={question}
                    sectionKey={sectionKey}
                    onUpdate={(updates) => onUpdateQuestion(sectionKey, questionId, updates)}
                    allQuestionIds={allQuestionIds}
                    errors={questionErrors}
                    depth={0}
                    isOrphan
                  />
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionAccordion;
